import { getNextDRScreen } from 'utils/drRedirection';
import { LoadableComponent } from '@loadable/component';
import { Navigate, useLocation } from 'react-router-dom';
import { ONE_TIME_TOKEN_LOGIN } from 'Router/routes';
import { useClient, useLatestLoan } from 'hooks/webapi';
import { useEffect, useState } from 'react';
import GuardRoute from './GuardRoute';
import isLogged from 'utils/isLogged';
import useClientDebtRestructuring from 'hooks/webapi/useClientDebtRestructuring';

type Props = {
  component: LoadableComponent<any>;
  path: string;
  fallback: JSX.Element;
  [key: string]: any; // for extra props passed to component
};

export default function PublicRoute({
  component: Component,
  path,
  ...restProps
}: Props) {
  const [shouldBeRedirected, setShouldBeRedirected] = useState(false);
  const { client } = useClient();
  const { pathname } = useLocation();
  const { debtRestructuringData } = useClientDebtRestructuring();
  const { latestLoan } = useLatestLoan();

  const basicInfoLoaded = Boolean(debtRestructuringData && client);

  const publicRouteAvailable =
    !isLogged() ||
    !basicInfoLoaded ||
    (client && client.status === 'REGISTERED');

  useEffect(() => {
    if (!publicRouteAvailable && pathname !== ONE_TIME_TOKEN_LOGIN) {
      const timer = setTimeout(() => {
        setShouldBeRedirected(true);
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setShouldBeRedirected(false);
    }
  }, [publicRouteAvailable, pathname]);

  // If logged in and data loaded → redirect after 1s delay
  if (shouldBeRedirected) {
    const dashboardRoute = getNextDRScreen(debtRestructuringData, latestLoan);
    return <Navigate to={dashboardRoute} replace />;
  }

  // Otherwise → show the public page
  return (
    <GuardRoute path={path}>
      <Component {...restProps} />
    </GuardRoute>
  );
}
